import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DocType, Language, Party } from 'api/file/interfaces/get-file-order-insights';
import clsx from 'clsx';
import DomesticAddressField from 'features/files/file-create/DomesticAddress';
import {
  docTypeOptions,
  EMAIL_VERIFICATION_SUBMITION_GUIDE,
  languageOptions,
  TEXT_VERIFICATION_SUBMITION_GUIDE,
} from 'features/help/constants';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import RadioGroup, { RadioProps } from 'ui-kit/components/radios/RadioGroup';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import PhoneField from 'ui-kit/inputs/PhoneField/PhoneField';
import {
  domesticAddressDrawerSchema,
  domesticAddressSchema,
  orderInsightEmailSchema,
  orderInsightPartySchema,
} from 'utilities/validation/schemas';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import OrderInsightAlert from './OrderInsightAlerts';

interface PhoneNumberDetailsProps {
  onConfirm: (value: Party, verifyWithEmail: boolean) => void;
  isActionInProgress: boolean;
  disablePrimary: boolean;
  party: Party;
  qaPrefix: string;
  hasAttemptErrors?: boolean;
  index: number;
  type: 'ContactValidation' | 'EnhancedIdentityVerification';
  syncPartyPhoneOrEmail: (updatedFields?: Partial<Party>, hasFieldRendered?: boolean) => void | boolean;
  updatedPartyInfo?: (updatedFields?: Partial<Party>) => void;
  validateFileErrors?: (currentParty: Party) => Promise<boolean>;
  showValidation?: (show: boolean) => void;
  isDrawer?: boolean;
  containerClassName?: string;
  disabled?: boolean;
  underwriterAlert?: ReactNode;
}

const identityVerificationDefaultOptions = {
  docType: DocType.NORTH_AMERICAN_DRIVER_LICENSE,
  language: Language.ENGLISH,
};

const useStyles = makeStyles((theme) => ({
  marginBottom: {
    marginBottom: theme.spacing(1),
  },
  submitButton: {
    marginTop: theme.spacing(1),
  },
  dialogWrap: {
    minWidth: 600,
  },
  alertContainer: {
    marginTop: theme.spacing(2.25),
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
  },
  dialogClose: {
    padding: 0,
  },
  dialogContent: {
    padding: pxToRem(24),
  },
}));

const PhoneNumberDetails = ({
  party,
  onConfirm,
  isActionInProgress,
  disablePrimary,
  type,
  qaPrefix,
  index,
  syncPartyPhoneOrEmail,
  updatedPartyInfo,
  hasAttemptErrors = false,
  validateFileErrors,
  showValidation,
  isDrawer = false,
  containerClassName,
  disabled = false,
  underwriterAlert,
}: PhoneNumberDetailsProps) => {
  const isContactValidation = type === 'ContactValidation';
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const { addSnackbarMessage } = useSnackBars();

  const preferredContactMethod = [
    {
      label: 'Text Message',
      value: 'phoneNumber',
      dataTestId: `${qaPrefix}-radiobutton-text-message${isDrawer ? '' : `-${index}`}`,
    },
    {
      label: 'Email',
      value: 'emailAddress',
      dataTestId: `${qaPrefix}-radiobutton-email${isDrawer ? '' : `-${index}`}`,
    },
  ];

  const [selectedPreferredContactMethod, setSelectedPreferredContactMethod] = useState<RadioProps>(
    preferredContactMethod[0],
  );

  const [isFormValid, setFormValid] = useState({
    isDomesticAddressFormValid: false,
    isPhoneOrEmailFormValid: false,
    fileSectionValid: true,
  });

  const [currentParty, setCurrentParty] = useState<Party>({
    ...party,
    ...(!isContactValidation
      ? {
          docType: party.docType ?? identityVerificationDefaultOptions.docType,
          language: party.language ?? identityVerificationDefaultOptions.language,
        }
      : {}),
  });

  useEffect(() => {
    // Sync entire party when it changes, especially for existing parties
    setCurrentParty({
      ...party,
      ...(!isContactValidation
        ? {
            docType: party.docType ?? identityVerificationDefaultOptions.docType,
            language: party.language ?? identityVerificationDefaultOptions.language,
          }
        : {}),
    });
  }, [party, isContactValidation]);

  useEffect(() => {
    const isFieldsRendered = syncPartyPhoneOrEmail(undefined, true);
    if (currentParty.phoneNumber !== party.phoneNumber && isFieldsRendered) {
      setCurrentParty((prev) => ({ ...prev, phoneNumber: party.phoneNumber }));
    }
  }, [party.phoneNumber, currentParty.phoneNumber]);

  useEffect(() => {
    const isFieldsRendered = syncPartyPhoneOrEmail(undefined, true);
    if (currentParty.emailAddress !== party.emailAddress && isFieldsRendered) {
      setCurrentParty((prev) => ({ ...prev, emailAddress: party.emailAddress }));
    }
  }, [party.emailAddress, currentParty.emailAddress]);

  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const classes = useStyles();

  const handledocTypeChange = (val: string) => {
    onChangeWithValidation({ ...currentParty, docType: val });
    updatedPartyInfo?.({ docType: val });
  };

  const handleLanguageChange = (val: string) => {
    onChangeWithValidation({ ...currentParty, language: val });
    updatedPartyInfo?.({ language: val });
  };

  const validateErrors = async (party: Party) => {
    const length = isDrawer ? 400 : 100;
    const isFileSectionValid = isDrawer && validateFileErrors ? await validateFileErrors(party) : true;
    const errs =
      (await doValidate(
        isContactValidation ? party : { ...party, preferredContactMethod: selectedPreferredContactMethod.value },
        isContactValidation ? orderInsightPartySchema(length) : orderInsightEmailSchema(length),
      )) || [];
    setValidationErrors(errs);
    setFormValid((prev) => ({
      ...prev,
      isPhoneOrEmailFormValid: errs.length === 0,
      fileSectionValid: isFileSectionValid,
    }));

    return isFileSectionValid && errs.length === 0;
  };

  const isPhoneNumberSelected = !isContactValidation && selectedPreferredContactMethod.value === 'phoneNumber';

  const isEmailAddressSelected = !isContactValidation && selectedPreferredContactMethod.value === 'emailAddress';

  const saveAndValidate = async () => {
    setIsSubmitted(true);
    showValidation?.(true);
    const isValid = await validateErrors(currentParty);
    if ((!isContactValidation || isFormValid.isDomesticAddressFormValid) && isValid) {
      onConfirm(currentParty, isEmailAddressSelected);
    } else if (isDrawer) {
      addSnackbarMessage({
        title: `Validation failed`,
        type: 'error',
      });
    }
  };

  const onChangeWithValidation = async (newValue: Party) => {
    validateErrors(newValue);
    setCurrentParty(newValue);
  };

  const handlePreferredContactMethod = (e: React.ChangeEvent<HTMLInputElement>) => {
    const paymentMethod =
      preferredContactMethod?.find((option) => option.value === e.target.value) ?? selectedPreferredContactMethod;
    setSelectedPreferredContactMethod(paymentMethod);
  };

  const isPhoneNumberRequired = useMemo(() => {
    if (
      !isContactValidation ||
      ((currentParty?.emailAddress?.trim() ?? '').length > 0 && (currentParty?.phoneNumber ?? '').length > 0)
    ) {
      return true;
    }

    return (currentParty?.emailAddress?.trim() ?? '').length === 0;
  }, [currentParty?.emailAddress, currentParty?.phoneNumber, type]);

  const isEmailAddressRequired = useMemo(() => {
    if (!isContactValidation) {
      return false;
    }

    if ((currentParty?.emailAddress?.trim() ?? '').length > 0 && (currentParty?.phoneNumber ?? '').length > 0) {
      return true;
    }

    return (currentParty?.phoneNumber ?? '').length === 0;
  }, [currentParty?.emailAddress, currentParty?.phoneNumber, type]);

  useEffect(() => {
    validateErrors(currentParty);
  }, [currentParty, selectedPreferredContactMethod?.value]);

  return (
    <>
      <Grid container spacing={2} className={clsx(classes.marginBottom, containerClassName)}>
        {!isContactValidation && (
          <Grid item sm={12} className="radioContainer">
            <RadioGroup
              name="preferredContactMethod"
              label="Preferred Contact Method"
              selected={selectedPreferredContactMethod}
              options={preferredContactMethod}
              onChange={handlePreferredContactMethod}
              disabled={disabled}
            />
          </Grid>
        )}
        {(isContactValidation || isPhoneNumberSelected) && (
          <Grid item xs={6}>
            <PhoneField
              variant="outlined"
              data-testid={`${qaPrefix}-input-phone-number${isDrawer ? '' : `-${index}`}`}
              required={isPhoneNumberRequired || isPhoneNumberSelected}
              name={'phoneNumber'}
              label={'Phone Number'}
              value={currentParty?.phoneNumber ?? ''}
              showValidation={isSubmitted}
              errs={validationErrors}
              fullWidth
              disabled={disabled}
              onChange={(e: any) => {
                onChangeWithValidation({ ...currentParty, phoneNumber: e?.target?.value });
                syncPartyPhoneOrEmail({ phoneNumber: e?.target?.value });
              }}
            />
          </Grid>
        )}
        {(isContactValidation || isEmailAddressSelected) && (
          <Grid item xs={6}>
            <AgentNetTextInput
              variant="outlined"
              fullWidth
              required={isEmailAddressRequired || isEmailAddressSelected}
              name="emailAddress"
              showValidation={isSubmitted}
              errs={validationErrors}
              data-testid={`${qaPrefix}-input-email${isDrawer ? '' : `-${index}`}`}
              label={'Email'}
              disabled={disabled}
              value={currentParty?.emailAddress?.trim() ?? ''}
              onChange={(e) => {
                onChangeWithValidation({ ...currentParty, emailAddress: e.target.value.trim() ?? '' });
                syncPartyPhoneOrEmail({ emailAddress: e.target.value.trim() ?? '' });
              }}
            />
          </Grid>
        )}
      </Grid>
      {isContactValidation ? (
        <>
          <Grid item xs={12} className={classes.marginBottom}>
            <DomesticAddressField
              showCounty={false}
              disabled={disabled}
              disableState={false}
              schema={isDrawer ? domesticAddressDrawerSchema : domesticAddressSchema}
              updatedQAAttributes={{
                address1: `${qaPrefix}-input-address1${isDrawer ? '' : `-${index}`}`,
                address2: `${qaPrefix}-input-address2${isDrawer ? '' : `-${index}`}`,
                city: `${qaPrefix}-input-city${isDrawer ? '' : `-${index}`}`,
                state: `${qaPrefix}-input-state${isDrawer ? '' : `-${index}`}`,
                postalCode: `${qaPrefix}-input-zip${isDrawer ? '' : `-${index}`}`,
                county: `${qaPrefix}-input-county${isDrawer ? '' : `-${index}`}`,
              }}
              isOutsideJurisdiction={true}
              setIsValid={(isValid) => {
                setFormValid((prev) => ({ ...prev, isDomesticAddressFormValid: isValid }));
              }}
              displayRequiredAddressFields={false}
              cityGridSize={6}
              value={{
                address1: currentParty.address?.address1 ?? '',
                address2: currentParty.address?.address2 ?? '',
                city: currentParty.address?.city ?? '',
                postalCode: currentParty.address?.postalCode ?? '',
                stateOrProvince: currentParty.address?.stateOrProvince ?? '',
              }}
              stateOptions={false}
              isPartyVerificationDrawer={isDrawer}
              showAllValidation={isSubmitted}
              useGoogleAutocomplete={true}
              onChange={(e) => {
                const updatedAddress = { ...currentParty.address, ...e };
                const updatedParty = { ...currentParty, address: updatedAddress };
                onChangeWithValidation(updatedParty);
                updatedPartyInfo?.({ address: updatedAddress });
              }}
              ContainerComponentProps={{
                spacing: 2,
              }}
            />
          </Grid>
          <AgentNetButton
            onClick={saveAndValidate}
            variant="contained"
            color="primary"
            className={classes.submitButton}
            data-testid={`${qaPrefix}-button-validate-contact-info${isDrawer ? '' : `-${index}`}`}
            size="small"
            disabled={isActionInProgress || disablePrimary || disabled}
            loading={isActionInProgress}
          >
            Validate Contact Info
          </AgentNetButton>
          {underwriterAlert && <Box className={classes.alertContainer}>{underwriterAlert}</Box>}
        </>
      ) : (
        <>
          <Grid container spacing={3} className={classes.marginBottom}>
            <Grid item sm={6}>
              <AgentNetDropdownSelector
                name="docType"
                label="Document Type"
                required
                options={docTypeOptions}
                data-testid={`${qaPrefix}-listbox-document-type${isDrawer ? '' : `-${index}`}`}
                value={currentParty?.docType}
                id="docType"
                menuOption={handledocTypeChange}
                dropdowntype="outlined"
                disabled={disabled}
              />
            </Grid>
            <Grid item sm={6}>
              <AgentNetDropdownSelector
                name="language"
                label="Language"
                required
                options={languageOptions}
                value={currentParty?.language}
                id="language"
                menuOption={handleLanguageChange}
                dropdowntype="outlined"
                disabled={disabled}
                data-testid={`${qaPrefix}-listbox-language${isDrawer ? '' : `-${index}`}`}
              />
            </Grid>
          </Grid>
          <AgentNetButton
            onClick={saveAndValidate}
            variant="contained"
            color="primary"
            data-testid={`${qaPrefix}-button-send-verification-request${isDrawer ? '' : `-${index}`}`}
            size="small"
            disabled={isActionInProgress || disablePrimary || disabled}
            loading={isActionInProgress}
          >
            Send Verification Request
          </AgentNetButton>
          {underwriterAlert && <Box className={classes.alertContainer}>{underwriterAlert}</Box>}
          {!hasAttemptErrors && (
            <Box className={classes.alertContainer}>
              <OrderInsightAlert
                alertWithContent={[
                  {
                    severity: 'info',
                    content: (
                      <>
                        <strong>Note: </strong>
                        {isEmailAddressSelected
                          ? EMAIL_VERIFICATION_SUBMITION_GUIDE
                          : TEXT_VERIFICATION_SUBMITION_GUIDE}
                      </>
                    ),
                  },
                ]}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default PhoneNumberDetails;
